import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useCreateAppTag } from "@/hooks/useAppTags";

interface CreateTagModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateTagModal({ open, onOpenChange }: CreateTagModalProps) {
  const [tagName, setTagName] = useState("");
  const [tagDescription, setTagDescription] = useState("");
  const [createdBy, setCreatedBy] = useState("");
  const [notes, setNotes] = useState("");

  const createMutation = useCreateAppTag();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!tagName.trim()) {
      return;
    }
    if (!tagDescription.trim()) {
      return;
    }
    if (tagName.trim().length > 255) {
      return;
    }

    try {
      await createMutation.mutateAsync({
        tag_name: tagName,
        tag_description: tagDescription,
        created_by: createdBy || undefined,
        notes: notes || undefined,
      });

      // Reset form and close modal
      setTagName("");
      setTagDescription("");
      setCreatedBy("");
      setNotes("");
      onOpenChange(false);
    } catch (error) {
      // Error handled by mutation
    }
  };

  const handleCancel = () => {
    setTagName("");
    setTagDescription("");
    setCreatedBy("");
    setNotes("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Create New Tag</DialogTitle>
            <DialogDescription>
              Add a new tag to the system. Tag name and description are required.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="tag-name">
                Tag Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="tag-name"
                value={tagName}
                onChange={(e) => setTagName(e.target.value)}
                placeholder="Enter tag name"
                maxLength={255}
                required
              />
              {tagName.trim().length === 0 && tagName.length > 0 && (
                <p className="text-sm text-red-500">Tag name is required</p>
              )}
              {tagName.trim().length > 255 && (
                <p className="text-sm text-red-500">Tag name must be 255 characters or less</p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="tag-description">
                Description <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="tag-description"
                value={tagDescription}
                onChange={(e) => setTagDescription(e.target.value)}
                placeholder="Enter tag description"
                rows={3}
                required
              />
              {tagDescription.trim().length === 0 && tagDescription.length > 0 && (
                <p className="text-sm text-red-500">Tag description is required</p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="created-by">Created By</Label>
              <Input
                id="created-by"
                value={createdBy}
                onChange={(e) => setCreatedBy(e.target.value)}
                placeholder="Enter your name (optional)"
                maxLength={255}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any additional notes (optional)"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                createMutation.isPending ||
                !tagName.trim() ||
                !tagDescription.trim() ||
                tagName.trim().length > 255
              }
            >
              {createMutation.isPending ? "Creating..." : "Create Tag"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
